import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    iso8601DateTimeZodValidator,
    uuidZodValidator,
} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";

export enum AnalyticalEngineDataType {
    text = "text",
    string = "string",
    numeric = "numeric",
    date = "date",
    timestamp = "timestamp",
    boolean = "boolean",
}

export enum AnalyticalEngineColumnType {
    metric = "metric",
    dimension = "dimension",
}

export enum AnalyticalEngineMetricAggregationType {
    sum = "sum",
    derived = "derived",
    weightedSum = "weighted_sum",
    average = "average",
    count = "count",
    distinctCount = "distinct_count",
    derivedCountDn = "derived_count_dn",
    derivedCountNd = "derived_count_nd",
    derivedCountNn = "derived_count_nn",
    derivedCountDd = "derived_count_dd",
    derivedCountSn = "derived_count_sn",
    derivedCountNs = "derived_count_ns",
    derivedCountSd = "derived_count_sd",
    derivedCountDs = "derived_count_ds",
}

export enum AnalyticalEngineGranularity {
    day = "Day",
    month = "Month",
    year = "Year",
}

export enum AnalyticalEngineOrderDirection {
    asc = "asc",
    desc = "desc",
}

export enum AnalyticalEngineAttributeRole {
    default = "258b412c-209a-4d6d-965c-640ac3d85e1a",
    enumeration = "01a41f26-23dd-4c83-b582-50fbfdbceef7",
    organizationalUnit = "33fe35de-087e-4dab-8adf-8776583d21e6",
    user = "a15eb246-0003-434a-8a1f-001cafcaa11f",
    productCatalogue = "8ff684a4-f951-4d15-90ae-a9b78cf14a48",
    userDefinedObject = "f5c14687-6e94-4e42-89a3-16696645bcbf",
    geographyCountries = "8a60312f-0523-4d57-8f79-d0817a7db16c",
    geographyIndiaCities = "1b413e07-f376-4a53-97b0-f27a1d6ead92",
    geographyIndiaPincodes = "366f7b74-503a-44a3-b05b-5693027c770a",
    geographyIndiaStates = "67e47305-27bc-4a5c-9b98-1a8c9f4ef2a3",
    geographyIndiaZones = "eff3d78a-bd5a-4b0d-b614-bdf9e69bd982",
    geographyLatitude = "84f5a172-aefa-4ee4-b38a-47a0eaccba82",
    geographyLongitude = "eeedbc7c-17eb-426b-8945-15843d04cf32",
    eventDisposition = "459638ea-37f3-4688-9101-2c8cd55498df",
    eventContainerDisposition = "4a7e54fc-5bc1-4270-b5bc-434ec0f09eb5",
}

export enum AnalyticalEngineAttributeEnumSource {
    organizationalUnitTemplate = "e0adf744-09c1-42dc-84d3-4dc354fd166a",
    userTemplate = "ae1f87b9-ec04-47e2-83f0-69ce492ef631",
    productCatalogueTemplate = "62c0363d-8e60-4852-a0d5-590330018331",
    userDefinedObjectTemplate = "4e0d4716-6d29-4d44-960e-421494168450",
    eventContainerTemplate = "37568831-b6f1-4582-9784-3125238a539f",
    eventTemplate = "aa473bda-c395-4e99-a18f-710fb83a578c",
}

export type AnalyticsDefaultSemantic = {
    attributeRole: AnalyticalEngineAttributeRole.default;
};

export type AnalyticsEnumerationSemantic = {
    attributeRole: AnalyticalEngineAttributeRole.enumeration;
    source: AnalyticalEngineAttributeEnumSource;
    templateId: Uuid;
    additionalColumnId: Uuid;
};

export type AnalyticsOrganizationalUnitSemantic = {
    attributeRole: AnalyticalEngineAttributeRole.organizationalUnit;
    organizationalUnitTemplateId: Uuid | null;
};

export type AnalyticsUserSemantic = {
    attributeRole: AnalyticalEngineAttributeRole.user;
    userTemplateId: Uuid | null;
};

export type AnalyticsProductCatalogueSemantic = {
    attributeRole: AnalyticalEngineAttributeRole.productCatalogue;
    productCatalogueTemplateId: Uuid | null;
};

export type AnalyticsUserDefinedObjectSemantic = {
    attributeRole: AnalyticalEngineAttributeRole.userDefinedObject;
    udoTemplateId: Uuid;
};

export type AnalyticsGeographyCountriesSemantic = {
    attributeRole: AnalyticalEngineAttributeRole.geographyCountries;
};

export type AnalyticsGeographyIndiaCitiesSemantic = {
    attributeRole: AnalyticalEngineAttributeRole.geographyIndiaCities;
};

export type AnalyticsGeographyIndiaPincodesSemantic = {
    attributeRole: AnalyticalEngineAttributeRole.geographyIndiaPincodes;
};

export type AnalyticsGeographyIndiaStatesSemantic = {
    attributeRole: AnalyticalEngineAttributeRole.geographyIndiaStates;
};

export type AnalyticsGeographyIndiaZonesSemantic = {
    attributeRole: AnalyticalEngineAttributeRole.geographyIndiaZones;
};

export type AnalyticsGeographyLatitudeSemantic = {
    attributeRole: AnalyticalEngineAttributeRole.geographyLatitude;
};

export type AnalyticsGeographyLongitudeSemantic = {
    attributeRole: AnalyticalEngineAttributeRole.geographyLongitude;
};

export type AnalyticsEventDispositionSemantic = {
    attributeRole: AnalyticalEngineAttributeRole.eventDisposition;
    eventTemplateId: Uuid;
};

export type AnalyticsEventContainerDispositionSemantic = {
    attributeRole: AnalyticalEngineAttributeRole.eventContainerDisposition;
    eventContainerTemplateId: Uuid;
};

export type DatasetAttributeSemantic =
    | AnalyticsDefaultSemantic
    | AnalyticsEnumerationSemantic
    | AnalyticsOrganizationalUnitSemantic
    | AnalyticsUserSemantic
    | AnalyticsProductCatalogueSemantic
    | AnalyticsUserDefinedObjectSemantic
    | AnalyticsGeographyCountriesSemantic
    | AnalyticsGeographyIndiaCitiesSemantic
    | AnalyticsGeographyIndiaPincodesSemantic
    | AnalyticsGeographyIndiaStatesSemantic
    | AnalyticsGeographyIndiaZonesSemantic
    | AnalyticsGeographyLatitudeSemantic
    | AnalyticsGeographyLongitudeSemantic
    | AnalyticsEventDispositionSemantic
    | AnalyticsEventContainerDispositionSemantic;

export function transformDatasetAttributeSemantic(semantic: DatasetAttributeSemantic): DatasetAttributeSemantic {
    return semantic;
}

export type AnalyticsMetric = {
    id: Uuid;
    displayName: string;
    columnName: string;
    type: AnalyticalEngineDataType;
    description: string;
    aggregationType: AnalyticalEngineMetricAggregationType | null;
    multiplier: number | null;
    numerator: string | null;
    denominator: string | null;
    metric: string | null;
    metricWeight: string | null;
    canonicalId: Uuid;
    agentContext?: string;
    semantic: DatasetAttributeSemantic;
    selector?: string;
    distributedSelector?: string | null;
};

export type AnalyticsDimension = {
    id: Uuid;
    displayName: string;
    columnName: string;
    type: AnalyticalEngineDataType;
    description: string;
    canonicalId: Uuid;
    agentContext?: string;
    semantic: DatasetAttributeSemantic;
};

export type AnalyticsDatasetSummary = {
    datasetId: Uuid;
    datasetTemplateId: Uuid | null;
    displayName: string;
    description: string;
    agentContext: string;
    imageDetails?: unknown | null;
};

export type AnalyticsDatasetDetails = AnalyticsDatasetSummary & {
    metrics: Record<string, AnalyticsMetric>;
    dimensions: Record<string, AnalyticsDimension>;
};

export type AnalyticsMetricSelection = {
    id: Uuid;
    distributed: boolean;
};

export type AnalyticsDimensionSelection = {
    id: Uuid;
};

export type AnalyticsOrderBy = {
    attribute: Uuid;
    direction?: AnalyticalEngineOrderDirection;
};

export enum AnalyticalEngineFilterOperation {
    equals = "bafb6b6c-90ca-42bc-9ee9-2ac60ff6c3e7",
    in = "4e61f5af-3ed9-4ae9-befd-d8fdcb73ebad",
    between = "1b40b607-8da0-48b0-9ab0-7fe53cab4a1d",
    and = "21049b0c-569f-4a37-9dc5-5fac882fff08",
    or = "a35b6797-6a09-4b61-bf7b-0ff1da3a01df",
    contains = "0e55fe86-1062-4fe2-95be-92b9ae0c71ea",
    greaterThan = "cdadd5e6-68f6-48e2-b56f-fc41ee91d2bb",
    lessThan = "f5aec8bc-42c4-4062-b82e-cd567d2e0091",
    greaterThanOrEqualTo = "15f683d5-f335-4ff6-8d4f-5cbe55e675bb",
    lessThanOrEqualTo = "f990d2dd-a7e5-46b7-887a-1c907c565e05",
}

export type AnalyticsFilter = {
    operation: AnalyticalEngineFilterOperation;
    attribute?: Uuid;
    value?: string;
    values?: Array<string>;
    filters?: Array<AnalyticsFilter>;
};

export type ListDatasetsRequest = {
    workspaceId: Uuid;
};

export type ListDatasetsResponse = Array<AnalyticsDatasetSummary>;

export type GetDatasetDetailsRequest = {
    workspaceId: Uuid;
    datasetId: Uuid;
};

export type GetDatasetDetailsResponse = AnalyticsDatasetDetails;

export type GetDistinctValuesRequest = {
    datasetId: Uuid;
    workspaceId: Uuid;
    attributeId: Uuid;
    limit?: number;
};

export type GetDistinctValuesResponse = Array<string | number | boolean | null>;

export type AnalyticsTimeRange = {
    startDate: Iso8601DateTime;
    endDate: Iso8601DateTime;
    granularity: AnalyticalEngineGranularity;
    timezone: string;
};

export type GetDatasetDataRequest = {
    datasetId: Uuid;
    workspaceId: Uuid;
    timeRange: AnalyticsTimeRange;
    accountId?: string;
    metrics: Array<AnalyticsMetricSelection>;
    dimensions: Array<AnalyticsDimensionSelection>;
    filter: AnalyticsFilter | null;
    orderBy?: Array<AnalyticsOrderBy>;
    limit?: number;
};

export type AnalyticsDataValue = string | number | boolean | null;

export type AnalyticsDataPoint = Array<{
    value: AnalyticsDataValue;
}>;

export type AnalyticsDataColumnMetadata = {
    id: Uuid;
    columnIndex: number;
    displayName: string;
    dataType: AnalyticalEngineDataType;
    columnType: AnalyticalEngineColumnType;
    name: string;
    agentContext?: string;
    semantic: DatasetAttributeSemantic;
};

export type GetDatasetDataResponse = {
    columnMetadata: Array<AnalyticsDataColumnMetadata>;
    dataPoints: Array<AnalyticsDataPoint>;
};

export type ResolveAnalyticalEngineSemanticValuesColumn = {
    columnRef: Uuid;
    semantic: DatasetAttributeSemantic;
    values: Array<Exclude<AnalyticsDataValue, null>>;
};

export type ResolveAnalyticalEngineSemanticValuesRequest = {
    columns: Array<ResolveAnalyticalEngineSemanticValuesColumn>;
};

export type ResolvedAnalyticalEngineSemanticValue = {
    rawValue: Exclude<AnalyticsDataValue, null>;
    displayValue: AnalyticsDataValue;
    resolved: boolean;
};

export type ResolvedAnalyticalEngineSemanticValuesColumn = {
    columnRef: Uuid;
    values: Array<ResolvedAnalyticalEngineSemanticValue>;
};

export type ResolveAnalyticalEngineSemanticValuesResponse = {
    columns: Array<ResolvedAnalyticalEngineSemanticValuesColumn>;
};

export const datasetAttributeSemanticValidator: zod.ZodType<DatasetAttributeSemantic> = zod.discriminatedUnion("attributeRole", [
    zod.strictObject({
        attributeRole: zod.literal(AnalyticalEngineAttributeRole.default),
    }),
    zod.strictObject({
        attributeRole: zod.literal(AnalyticalEngineAttributeRole.enumeration),
        source: zod.enum(AnalyticalEngineAttributeEnumSource),
        templateId: uuidZodValidator,
        additionalColumnId: uuidZodValidator,
    }),
    zod.strictObject({
        attributeRole: zod.literal(AnalyticalEngineAttributeRole.organizationalUnit),
        organizationalUnitTemplateId: uuidZodValidator.nullable(),
    }),
    zod.strictObject({
        attributeRole: zod.literal(AnalyticalEngineAttributeRole.user),
        userTemplateId: uuidZodValidator.nullable(),
    }),
    zod.strictObject({
        attributeRole: zod.literal(AnalyticalEngineAttributeRole.productCatalogue),
        productCatalogueTemplateId: uuidZodValidator.nullable(),
    }),
    zod.strictObject({
        attributeRole: zod.literal(AnalyticalEngineAttributeRole.userDefinedObject),
        udoTemplateId: uuidZodValidator,
    }),
    zod.strictObject({
        attributeRole: zod.literal(AnalyticalEngineAttributeRole.geographyCountries),
    }),
    zod.strictObject({
        attributeRole: zod.literal(AnalyticalEngineAttributeRole.geographyIndiaCities),
    }),
    zod.strictObject({
        attributeRole: zod.literal(AnalyticalEngineAttributeRole.geographyIndiaPincodes),
    }),
    zod.strictObject({
        attributeRole: zod.literal(AnalyticalEngineAttributeRole.geographyIndiaStates),
    }),
    zod.strictObject({
        attributeRole: zod.literal(AnalyticalEngineAttributeRole.geographyIndiaZones),
    }),
    zod.strictObject({
        attributeRole: zod.literal(AnalyticalEngineAttributeRole.geographyLatitude),
    }),
    zod.strictObject({
        attributeRole: zod.literal(AnalyticalEngineAttributeRole.geographyLongitude),
    }),
    zod.strictObject({
        attributeRole: zod.literal(AnalyticalEngineAttributeRole.eventDisposition),
        eventTemplateId: uuidZodValidator,
    }),
    zod.strictObject({
        attributeRole: zod.literal(AnalyticalEngineAttributeRole.eventContainerDisposition),
        eventContainerTemplateId: uuidZodValidator,
    }),
]);

const nonNullAnalyticsDataValueValidator = zod.union([zod.string(), zod.number(), zod.boolean()]);

export const resolveAnalyticalEngineSemanticValuesColumnValidator: zod.ZodType<ResolveAnalyticalEngineSemanticValuesColumn> = zod.strictObject({
    columnRef: uuidZodValidator,
    semantic: datasetAttributeSemanticValidator,
    values: nonNullAnalyticsDataValueValidator.array().max(1_000).superRefine((values, context) => {
        const uniqueValues = new Set(values.map((value) => `${typeof value}:${String(value)}`));
        if (uniqueValues.size !== values.length) {
            context.addIssue({
                code: "custom",
                message: "Semantic values must be unique within a column",
            });
        }
    }),
});

export const resolveAnalyticalEngineSemanticValuesRequestValidator: zod.ZodType<ResolveAnalyticalEngineSemanticValuesRequest> = zod.strictObject({
    columns: resolveAnalyticalEngineSemanticValuesColumnValidator.array().max(100).superRefine((columns, context) => {
        const columnRefs = new Set(columns.map((column) => column.columnRef));
        if (columnRefs.size !== columns.length) {
            context.addIssue({
                code: "custom",
                message: "Semantic-value column references must be unique",
            });
        }
        if (columns.reduce((total, column) => total + column.values.length, 0) > 5_000) {
            context.addIssue({
                code: "custom",
                message: "A semantic-value request may contain at most 5000 values",
            });
        }
    }),
});

export const resolvedAnalyticalEngineSemanticValueValidator: zod.ZodType<ResolvedAnalyticalEngineSemanticValue> = zod.strictObject({
    rawValue: nonNullAnalyticsDataValueValidator,
    displayValue: zod.union([zod.string(), zod.number(), zod.boolean(), zod.null()]),
    resolved: zod.boolean(),
});

export const resolveAnalyticalEngineSemanticValuesResponseValidator: zod.ZodType<ResolveAnalyticalEngineSemanticValuesResponse> = zod.strictObject({
    columns: zod.strictObject({
        columnRef: uuidZodValidator,
        values: resolvedAnalyticalEngineSemanticValueValidator.array(),
    }).array(),
});

export const analyticsMetricValidator: zod.ZodType<AnalyticsMetric> = zod.object({
    id: uuidZodValidator,
    displayName: zod.string(),
    columnName: zod.string(),
    type: zod.enum(AnalyticalEngineDataType),
    description: zod.string(),
    aggregationType: zod.enum(AnalyticalEngineMetricAggregationType).nullable().optional().transform((value) => value ?? null),
    multiplier: zod.number().nullable().optional().transform((value) => value ?? null),
    numerator: zod.string().nullable().optional().transform((value) => value ?? null),
    denominator: zod.string().nullable().optional().transform((value) => value ?? null),
    metric: zod.string().nullable().optional().transform((value) => value ?? null),
    metricWeight: zod.string().nullable().optional().transform((value) => value ?? null),
    canonicalId: uuidZodValidator,
    agentContext: zod.string().optional(),
    semantic: datasetAttributeSemanticValidator,
    selector: zod.string().optional(),
    distributedSelector: zod.string().nullable().optional(),
});

export const analyticsDimensionValidator: zod.ZodType<AnalyticsDimension> = zod.object({
    id: uuidZodValidator,
    displayName: zod.string(),
    columnName: zod.string(),
    type: zod.enum(AnalyticalEngineDataType),
    description: zod.string(),
    canonicalId: uuidZodValidator,
    agentContext: zod.string().optional(),
    semantic: datasetAttributeSemanticValidator,
});

const analyticsDatasetSummarySchema = zod.object({
    datasetId: uuidZodValidator,
    datasetTemplateId: uuidZodValidator.nullable(),
    displayName: zod.string(),
    description: zod.string(),
    agentContext: zod.string(),
    imageDetails: zod.unknown().nullable().optional(),
});

export const analyticsDatasetSummaryValidator: zod.ZodType<AnalyticsDatasetSummary> = analyticsDatasetSummarySchema;

export const analyticsDatasetDetailsValidator: zod.ZodType<AnalyticsDatasetDetails> = analyticsDatasetSummarySchema.extend({
    metrics: zod.record(zod.string(), analyticsMetricValidator),
    dimensions: zod.record(zod.string(), analyticsDimensionValidator),
});

export const analyticsMetricSelectionValidator: zod.ZodType<AnalyticsMetricSelection> = zod.strictObject({
    id: uuidZodValidator,
    distributed: zod.boolean().default(false),
});

export const analyticsDimensionSelectionValidator: zod.ZodType<AnalyticsDimensionSelection> = zod.strictObject({
    id: uuidZodValidator,
});

export const analyticsOrderByValidator: zod.ZodType<AnalyticsOrderBy> = zod.strictObject({
    attribute: uuidZodValidator,
    direction: zod.enum(AnalyticalEngineOrderDirection).optional(),
});

export const analyticsFilterValidator: zod.ZodType<AnalyticsFilter> = zod.lazy(() => zod.object({
    operation: zod.enum(AnalyticalEngineFilterOperation),
    attribute: uuidZodValidator.optional(),
    value: zod.string().optional(),
    values: zod.string().array().optional(),
    filters: analyticsFilterValidator.array().optional(),
}));

export const listDatasetsRequestValidator: zod.ZodType<ListDatasetsRequest> = zod.strictObject({
    workspaceId: uuidZodValidator,
});

export const listDatasetsResponseValidator: zod.ZodType<ListDatasetsResponse> = analyticsDatasetSummaryValidator.array();

export const getDatasetDetailsRequestValidator: zod.ZodType<GetDatasetDetailsRequest> = zod.strictObject({
    workspaceId: uuidZodValidator,
    datasetId: uuidZodValidator,
});

export const getDatasetDetailsResponseValidator: zod.ZodType<GetDatasetDetailsResponse> = analyticsDatasetDetailsValidator;

export const getDistinctValuesRequestValidator: zod.ZodType<GetDistinctValuesRequest> = zod.strictObject({
    datasetId: uuidZodValidator,
    workspaceId: uuidZodValidator,
    attributeId: uuidZodValidator,
    limit: zod.number().int().positive().optional(),
});

export const getDistinctValuesResponseValidator: zod.ZodType<GetDistinctValuesResponse> = zod.union([
    zod.string(),
    zod.number(),
    zod.boolean(),
    zod.null(),
]).array();

export const analyticsTimeRangeValidator: zod.ZodType<AnalyticsTimeRange> = zod.strictObject({
    startDate: iso8601DateTimeZodValidator,
    endDate: iso8601DateTimeZodValidator,
    granularity: zod.enum(AnalyticalEngineGranularity),
    timezone: zod.string().min(1),
});

export const getDatasetDataRequestValidator: zod.ZodType<GetDatasetDataRequest> = zod.strictObject({
    datasetId: uuidZodValidator,
    workspaceId: uuidZodValidator,
    timeRange: analyticsTimeRangeValidator,
    accountId: zod.string().optional(),
    metrics: analyticsMetricSelectionValidator.array(),
    dimensions: analyticsDimensionSelectionValidator.array(),
    filter: analyticsFilterValidator.nullable(),
    orderBy: analyticsOrderByValidator.array().optional(),
    limit: zod.number().int().positive().optional(),
});

export const analyticsDataPointValidator: zod.ZodType<AnalyticsDataPoint> = zod.object({
    value: zod.union([zod.string(), zod.number(), zod.boolean(), zod.null()]),
}).array();

export const analyticsDataColumnMetadataValidator: zod.ZodType<AnalyticsDataColumnMetadata> = zod.object({
    id: uuidZodValidator,
    columnIndex: zod.number().int(),
    displayName: zod.string(),
    dataType: zod.enum(AnalyticalEngineDataType),
    columnType: zod.enum(AnalyticalEngineColumnType),
    name: zod.string(),
    agentContext: zod.string().optional(),
    semantic: datasetAttributeSemanticValidator,
});

export const getDatasetDataResponseValidator: zod.ZodType<GetDatasetDataResponse> = zod.strictObject({
    columnMetadata: analyticsDataColumnMetadataValidator.array(),
    dataPoints: analyticsDataPointValidator.array(),
});
